import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtDate, CAD } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ChevronLeft, Send, Plus, Upload, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { ExpiryBanner } from "../components/ExpiryBanner";
import DocViewerModal from "../components/DocViewerModal";
import ExpiryDocCard from "../components/ExpiryDocCard";
import DeferredDialog from "../components/DeferredDialog";

const STAGES = ["New Application","Documents Pending","Applied","Additional Documents Requested","Conditional Offer","Unconditional Offer","Fees Pending","Fees Paid","Admission Complete","Admission Complete / PAL Awaited","Deferred","Withdrawn","Closed"];
const FILING_STATUSES = ["New Application","Documents Pending","Applied","Documents Requested","Approved","Rejected","Withdrawn"];

export default function B2CApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffFO, setHandoffFO] = useState("");
  const [filingModal, setFilingModal] = useState(null); // {status, form, files:{doc_type:File}}
  const [appliedModal, setAppliedModal] = useState(null); // {college_app_id}
  const [semDialog, setSemDialog] = useState(null); // {mode: create|update, sem, form}
  const [commReceivedDialog, setCommReceivedDialog] = useState(null); // {claim_id, expected, date, amount}
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignOfficer, setReassignOfficer] = useState("");
  const [docViewer, setDocViewer] = useState(null); // { url, title }
  const [deferredOpen, setDeferredOpen] = useState(false);
  const [deferredSaving, setDeferredSaving] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/b2c/applications/${id}`), api.get("/b2c/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!data || !meta) return <div>Loading…</div>;
  const a = data.application;
  const perms = data.permissions;
  const filingOfficers = meta.users.filter(u => u.role === "filing_officer" && u.is_active !== false && ["B2C","BOTH"].includes((u.line_of_business||"BOTH").toUpperCase()));
  const admissionsOfficers = meta.users.filter(u => u.role === "admissions_officer" && u.is_active !== false && ["B2C","BOTH"].includes((u.line_of_business||"BOTH").toUpperCase()));
  const canReassign = ["admin","manager"].includes(me?.role || "");

  const patch = async (body) => { await api.patch(`/b2c/applications/${id}`, body); load(); };

  const changeStage = async (newStage) => {
    if (newStage === "Applied") {
      setAppliedModal({ college_app_id: a.college_app_id || "" });
      return;
    }
    if (newStage === "Deferred") {
      setDeferredOpen(true);
      return;
    }
    await patch({ stage: newStage });
  };

  const confirmDeferred = async (deferredTo) => {
    setDeferredSaving(true);
    try {
      await api.patch(`/b2c/applications/${id}`, { stage: "Deferred", deferred_to: deferredTo });
      toast.success(`Deferred to ${deferredTo}`);
      setDeferredOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to defer");
    } finally {
      setDeferredSaving(false);
    }
  };

  const submitApplied = async () => {
    if (!appliedModal?.college_app_id?.trim()) { toast.error("College Application ID is required"); return; }
    await api.patch(`/b2c/applications/${id}`, { stage: "Applied", college_app_id: appliedModal.college_app_id.trim() });
    setAppliedModal(null); load();
  };

  const uploadFileNow = async (docType, fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    await api.post(`/b2c/applications/${id}/upload?doc_type=${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const submitHandoff = async () => {
    if (!handoffFO) return toast.error("Filing Officer required");
    try { await api.post(`/b2c/applications/${id}/handoff-filing`, { filing_officer_id: handoffFO }); toast.success("Handed off to Filing"); setHandoffOpen(false); setHandoffFO(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const REQUIRED_DOCS = {
    "Applied": [{ key: "submission_letter", label: "Submission Letter (PDF) *" }],
    "Approved": [{ key: "approval_letter", label: "Approval Letter (PDF) *" }],
    "Rejected": [{ key: "rejection_letter", label: "Rejection/Refusal Letter (PDF) *" }, { key: "odn", label: "ODN (PDF) *" }],
    "Withdrawn": [{ key: "withdrawal_letter", label: "Withdrawal Letter (PDF) *" }],
    "Documents Requested": [{ key: "request_document", label: "Request Document (PDF) *" }],
  };

  const submitFiling = async () => {
    const st = filingModal.status;
    const required = REQUIRED_DOCS[st] || [];
    const files = filingModal.files || {};
    for (const rq of required) {
      if (!files[rq.key]) { toast.error(`${rq.label.replace(" *","")} is required`); return; }
      const f = files[rq.key];
      const okExt = /\.(pdf|jpg|jpeg|png)$/i.test(f.name);
      if (!okExt) { toast.error(`${rq.label.replace(" *","")}: only PDF/JPG/PNG accepted`); return; }
    }
    try {
      for (const rq of required) { await uploadFileNow(rq.key, files[rq.key]); }
      await api.post(`/b2c/applications/${id}/filing`, { status: st, ...filingModal.form });
      toast.success("Filing updated"); setFilingModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadDoc = async (docType, file = null) => {
    const f = file || fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file");
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/b2c/applications/${id}/upload?doc_type=${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); if (fileRef.current) fileRef.current.value = ""; load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pickAndUpload = (docType) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = ".pdf,.jpg,.jpeg,.png";
    inp.onchange = (e) => { const f = e.target.files?.[0]; if (f) uploadDoc(docType, f); };
    inp.click();
  };
  const downloadDoc = async (f) => {
    const r = await api.get(`/b2c/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a"); link.href = url; link.download = f.original_filename; link.click();
    URL.revokeObjectURL(url);
  };

  const canHandoff = ["Admission Complete","Admission Complete / PAL Awaited"].includes(a.stage) && !a.filing_started && perms.admissions === "edit";

  return (
    <div className="space-y-6">
      <Link to="/b2c/applications" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4" /> Back to applications</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={()=>nav(`/b2c/students/${data.student.id}`)} className="heading-font text-2xl font-bold text-slate-900 hover:text-orange-600">{data.student?.full_name}</button>
              <span className="mono text-xs text-slate-500">{a.application_id}</span>
              <span className="mono text-xs text-slate-400">{data.student?.student_id}</span>
            </div>
            <div className="text-sm text-slate-500 mt-1" data-testid="app-header-line">
              {data.college?.name} · {data.program?.name} · {data.intake?.name}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="stage-badge bg-blue-50 text-blue-900 border-blue-300" data-testid="stage-badge">{a.stage}{a.stage === "Deferred" && a.deferred_to ? ` → ${a.deferred_to}` : ""}</span>
              {a.stage === "Deferred" && a.deferred_to && (
                <span className="stage-badge bg-amber-50 text-amber-900 border-amber-300 font-semibold" data-testid="deferred-to-badge">Deferred to {a.deferred_to}</span>
              )}
              {a.filing_started && <span className="stage-badge bg-purple-50 text-purple-900 border-purple-300">Filing: {a.filing_status}</span>}
              {a.pal_pending && <span className="stage-badge bg-amber-50 text-amber-900 border-amber-300 font-semibold">PAL PENDING</span>}
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {perms.admissions === "edit" && (
              <Select value={a.stage} onValueChange={changeStage}>
                <SelectTrigger data-testid="b2c-stage-select" className="h-9 w-60"><SelectValue/></SelectTrigger>
                <SelectContent>{STAGES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {a.stage === "Deferred" && a.deferred_to && perms.admissions === "edit" && (
              <Button variant="outline" size="sm" data-testid="edit-deferred-btn" onClick={()=>setDeferredOpen(true)}>Edit deferred intake</Button>
            )}
            {canHandoff && (
              <Button data-testid="handoff-filing-btn" onClick={()=>setHandoffOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Send className="w-4 h-4 mr-2"/>Move to Filing</Button>
            )}
            {canReassign && (
              <Button data-testid="reassign-officer-btn" variant="outline" size="sm" onClick={()=>{ setReassignOfficer(a.admission_officer_id || ""); setReassignOpen(true); }}>
                Reassign Officer
              </Button>
            )}
          </div>
        </div>
      </div>

      <ExpiryDocCard
        applicationId={id}
        uploadPath={`/b2c/applications/${id}/upload`}
        downloadPath={`/b2c/files`}
        docType="Expiry Document"
        dateField="visa_expiry"
        dateLabel="Visa Expiry"
        expiryLabel="Current Visa / Permit Expiry"
        contextChips={[
          data.college?.name && { label: "College", value: data.college.name },
          data.program?.name && { label: "Program", value: data.program.name },
          data.intake?.name && { label: "Intake", value: data.intake.name },
        ].filter(Boolean)}
        initialDate={a.visa_expiry}
        initialFileId={a.expiry_doc_file_id}
        initialFileName={a.expiry_doc_file_name}
        onSaved={()=>load()}
      />

      <Tabs defaultValue="admission">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          {perms.admissions !== "none" && <TabsTrigger value="admission" data-testid="tab-admission">Admission</TabsTrigger>}
          {perms.filing !== "none" && <TabsTrigger value="filing" data-testid="tab-filing">Filing {a.pal_pending && <span className="ml-1 stage-badge bg-amber-100 text-amber-900 border-amber-400">PAL</span>}</TabsTrigger>}
          {perms.enrollment !== "none" && <TabsTrigger value="enrollment" data-testid="tab-enrollment">Enrollment ({data.semesters.length})</TabsTrigger>}
          {perms.commission !== "none" && <TabsTrigger value="commission" data-testid="tab-commission">Commission</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({data.documents.length})</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Info label="Student" value={data.student?.full_name} />
            <Info label="College" value={data.college?.name} />
            <Info label="Program" value={data.program?.name} />
            <Info label="Intake" value={data.intake?.name} />
            <Info label="Priority" value={a.priority} />
            <Info label="Filing Status" value={a.filing_status} />
            <Info label="PAL Pending" value={a.pal_pending ? "Yes" : "No"} />
          </div>
        </TabsContent>

        <TabsContent value="admission">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Priority">
              <Select value={a.priority} onValueChange={v=>patch({priority:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Low","Medium","High","Urgent"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="College App ID"><Input defaultValue={a.college_app_id||""} onBlur={e=>patch({college_app_id:e.target.value})}/></F>
            <F label="Admission Officer">
              {canReassign ? (
                <Select value={a.admission_officer_id||""} onValueChange={v=>patch({admission_officer_id:v})}>
                  <SelectTrigger data-testid="admission-officer-select"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                  <SelectContent>{meta.users.filter(u=>["admissions_officer","admin","manager"].includes(u.role)).map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (() => {
                const assigned = meta.users.find(u => u.user_id === a.admission_officer_id);
                return (
                  <div data-testid="admission-officer-readonly" className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center">
                    {assigned ? assigned.name : <span className="text-slate-400 italic">Unassigned</span>}
                    <span className="ml-auto text-xs text-slate-500">🔒 Admin/Manager only</span>
                  </div>
                );
              })()}
            </F>
            <div className="md:col-span-2"><F label="Notes"><Textarea defaultValue={a.notes||""} onBlur={e=>e.target.value!==a.notes && patch({notes:e.target.value})}/></F></div>
          </div>
        </TabsContent>

        <TabsContent value="filing">
          {!a.filing_started ? (
            <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Filing has not been started. Reach Admission Complete or Admission Complete / PAL Awaited and click "Move to Filing".</div>
          ) : (
            <div className="space-y-4">
              {/* Current status + officer + PAL */}
              <div className="bg-white p-5 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current Filing Status</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`stage-badge text-sm font-semibold ${
                        a.filing_status === "Approved" ? "bg-emerald-50 text-emerald-900 border-emerald-300" :
                        a.filing_status === "Rejected" ? "bg-rose-50 text-rose-900 border-rose-300" :
                        a.filing_status === "Withdrawn" ? "bg-slate-100 text-slate-700 border-slate-300" :
                        a.filing_status === "Applied" ? "bg-blue-50 text-blue-900 border-blue-300" :
                        a.filing_status === "Documents Requested" ? "bg-amber-50 text-amber-900 border-amber-300" :
                        "bg-purple-50 text-purple-900 border-purple-300"
                      }`}>{a.filing_status || "—"}</span>
                      {a.pal_pending && <span className="stage-badge bg-amber-100 text-amber-900 border-amber-400 font-semibold">PAL PENDING</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-2">Filing Officer: <span className="font-medium text-slate-700">{meta.users.find(u=>u.user_id===a.filing_officer_id)?.name || "—"}</span></div>
                    {perms.filing !== "edit" && (
                      <div className="text-[11px] text-slate-400 italic">You have view-only access to Filing. Reach out to the filing team to make updates.</div>
                    )}
                  </div>
                  {perms.filing === "edit" && (
                    <div className="flex flex-wrap gap-2">
                      {FILING_STATUSES.map(s => (
                        <Button key={s} variant={a.filing_status===s?"default":"outline"} size="sm"
                                data-testid={`filing-btn-${s.replace(/ /g,'-')}`}
                                onClick={()=>{
                                  if (["Applied","Approved","Rejected","Withdrawn","Documents Requested"].includes(s)) setFilingModal({status: s, form:{}, files:{}});
                                  else api.post(`/b2c/applications/${id}/filing`, { status: s }).then(load).catch(e=>toast.error(e.response?.data?.detail||"Failed"));
                                }}>
                          {s}
                        </Button>
                      ))}
                      {a.pal_pending && <Button size="sm" onClick={async()=>{ await api.post(`/b2c/applications/${id}/filing`, { status: a.filing_status || "New Application", pal_received: true }); toast.success("PAL received"); load(); }}>Mark PAL Received</Button>}
                    </div>
                  )}
                </div>
              </div>

              {/* Filing key facts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {a.ircc_file_number && <Info label="IRCC File Number" value={a.ircc_file_number}/>}
                {a.submission_date && <Info label="Submission Date" value={fmtDate(a.submission_date)}/>}
                {a.approval_date && <Info label="Approval Date" value={fmtDate(a.approval_date)}/>}
                {a.rejection_date && <Info label="Rejection Date" value={fmtDate(a.rejection_date)}/>}
                {a.withdrawal_date && <Info label="Withdrawn Date" value={fmtDate(a.withdrawal_date)}/>}
                {a.pal_received_date && <Info label="PAL Received" value={fmtDate(a.pal_received_date)}/>}
              </div>

              {/* Filing timeline (visible to everyone, including view-only) */}
              {(() => {
                const filingEvents = (data.activity || []).filter(ev =>
                  (ev.entity === "filing") || (ev.entity === "application" && ev.action === "filing_handoff")
                );
                if (filingEvents.length === 0) {
                  return (
                    <div className="bg-white p-4 rounded-xl border border-slate-200 text-xs text-slate-500">
                      No filing history yet. Updates will appear here as the filing team progresses.
                    </div>
                  );
                }
                return (
                  <div className="bg-white p-5 rounded-xl border border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Filing Timeline</div>
                    <ol className="relative border-l border-slate-200 ml-2">
                      {filingEvents.map((ev, i) => {
                        const to = ev.new_value;
                        const from = ev.old_value;
                        const dotCls =
                          typeof to === "string" && /approv/i.test(to) ? "bg-emerald-500" :
                          typeof to === "string" && /reject/i.test(to) ? "bg-rose-500" :
                          typeof to === "string" && /withdraw/i.test(to) ? "bg-slate-400" :
                          typeof to === "string" && /appli/i.test(to) ? "bg-blue-500" :
                          "bg-purple-500";
                        return (
                          <li key={ev.id || i} className="ml-4 mb-4 last:mb-0">
                            <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 border-white ${dotCls}`} />
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-800">
                                {ev.action === "filing_handoff" ? "Handed off to filing"
                                  : from ? <>Status: <span className="text-slate-500 line-through">{typeof from === "string" ? from : JSON.stringify(from)}</span> → <span className="text-slate-900">{typeof to === "string" ? to : JSON.stringify(to)}</span></>
                                  : <>Status: {typeof to === "string" ? to : JSON.stringify(to)}</>}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {fmtDate(ev.created_at)} · by {ev.actor_name || ev.actor_email || "system"}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                );
              })()}

              {/* Filing documents (view-only summary) */}
              {(() => {
                const filingDocs = (data.files || []).filter(f => ["submission_letter","approval_letter","rejection_letter"].includes(f.doc_type));
                if (filingDocs.length === 0) return null;
                return (
                  <div className="bg-white p-4 rounded-xl border border-slate-200">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Filing Documents</div>
                    <div className="space-y-1.5">
                      {filingDocs.map(f => (
                        <div key={f.id} className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-xs uppercase text-slate-500 font-semibold min-w-[130px]">{f.doc_type.replace(/_/g," ")}</span>
                          <span className="flex-1 truncate text-slate-700" title={f.original_filename}>{f.original_filename}</span>
                          <span className="text-xs text-slate-400">{fmtDate(f.created_at)}</span>
                          <Button size="sm" variant="outline" onClick={async()=>{ const r = await api.get(`/b2c/files/${f.id}/download`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); setDocViewer({ url: u, title: f.original_filename }); }}>
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </TabsContent>

        <TabsContent value="enrollment">
          <div className="space-y-3">
            {perms.enrollment === "edit" && data.semesters.length > 0 && !data.semesters.find(s=>s.semester_number === (Math.max(...data.semesters.map(s=>s.semester_number))+1)) && (
              <Button data-testid="add-next-sem" onClick={()=>{
                const next = Math.max(...data.semesters.map(s=>s.semester_number))+1;
                if (next>4) return toast.error("Max 4 semesters");
                setSemDialog({mode:"create", form:{semester_number: next, intake_id: meta.intakes[0]?.id}});
              }} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add Next Semester</Button>
            )}
            {data.semesters.map(sem => (
              <div key={sem.id} className="bg-white p-5 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="heading-font font-semibold">Semester {sem.semester_number}</div>
                    <div className="text-xs text-slate-500 mt-1">Original: {meta.intakes.find(i=>i.id===sem.original_intake_id)?.name} · Current: {meta.intakes.find(i=>i.id===sem.current_intake_id)?.name}</div>
                    {sem.enrollment_confirmation_date && <div className="text-xs text-slate-500">Confirmed: {fmtDate(sem.enrollment_confirmation_date)}</div>}
                    {sem.deferred_intake_id && <div className="text-xs text-slate-500">Deferred: {fmtDate(sem.deferred_date)} → {meta.intakes.find(i=>i.id===sem.deferred_intake_id)?.name}</div>}
                    {sem.withdrawal_reason && <div className="text-xs text-slate-500">Withdrawn: {sem.withdrawal_reason}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`stage-badge ${sem.status==="ENROLLED"?"bg-emerald-50 text-emerald-900 border-emerald-300":sem.status==="DEFERRED"?"bg-amber-50 text-amber-900 border-amber-300":sem.status==="WITHDRAWN"?"bg-red-50 text-red-900 border-red-300":"bg-slate-50 text-slate-800 border-slate-300"}`}>{sem.status}</span>
                    {perms.enrollment === "edit" && (
                      <Select value={sem.status} onValueChange={v=>{
                        if (v==="ENROLLED") setSemDialog({mode:"update", sem, form:{status:"ENROLLED"}});
                        else if (v==="DEFERRED") setSemDialog({mode:"update", sem, form:{status:"DEFERRED"}});
                        else if (v==="WITHDRAWN") setSemDialog({mode:"update", sem, form:{status:"WITHDRAWN"}});
                        else api.patch(`/b2c/semesters/${sem.id}`, { status: v }).then(load);
                      }}>
                        <SelectTrigger data-testid={`sem-${sem.semester_number}-status`} className="h-9 w-40"><SelectValue/></SelectTrigger>
                        <SelectContent>{["PENDING","ENROLLED","DEFERRED","WITHDRAWN"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {data.semesters.length===0 && <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Semester 1 is auto-created when Filing becomes Approved.</div>}
          </div>
        </TabsContent>

        <TabsContent value="commission">
          {perms.commission === "none" && <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Access restricted.</div>}
          {perms.commission !== "none" && (
            <div className="space-y-3">
              {data.commissions.map(c => (
                <div key={c.id} className="bg-white p-5 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="heading-font font-semibold">Semester {c.semester_number} Claim</div>
                      <div className="text-xs text-slate-500 mt-1">Invoice: {c.invoice_number || "—"} · {c.invoice_date ? fmtDate(c.invoice_date) : ""}</div>
                      {c.amount_received != null && <div className="text-xs text-slate-500">Received: {CAD(c.amount_received)} on {fmtDate(c.commission_received_date)}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Expected</div>
                        <div className="mono font-semibold">{c.expected_commission_amount != null ? CAD(c.expected_commission_amount) : "—"}</div>
                      </div>
                      <span className="stage-badge bg-teal-50 text-teal-900 border-teal-300">{c.status}</span>
                    </div>
                  </div>
                  {perms.commission === "edit" && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <F label="Expected (CAD)"><Input type="number" defaultValue={c.expected_commission_amount ?? ""} onBlur={async e=>{ await api.patch(`/b2c/commission-claims/${c.id}`, { expected_commission_amount: Number(e.target.value) }); load(); }}/></F>
                      <F label="Invoice #"><Input defaultValue={c.invoice_number||""} onBlur={async e=>{ await api.patch(`/b2c/commission-claims/${c.id}`, { invoice_number: e.target.value }); load(); }}/></F>
                      <F label="Status">
                        <Select value={c.status} onValueChange={async v=>{
                          if (v==="COMMISSION RECEIVED") {
                            setCommReceivedDialog({
                              claim_id: c.id,
                              expected: c.expected_commission_amount ?? 0,
                              amount: c.expected_commission_amount ?? "",
                              date: new Date().toISOString().slice(0,10),
                              semester: c.semester_number,
                            });
                            return;
                          }
                          await api.patch(`/b2c/commission-claims/${c.id}`, { status: v });
                          load();
                        }}>
                          <SelectTrigger data-testid={`claim-${c.semester_number}-status`}><SelectValue/></SelectTrigger>
                          <SelectContent>{["INVOICE PENDING","INVOICE SENT","COMMISSION RECEIVED"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </F>
                    </div>
                  )}
                </div>
              ))}
              {data.commissions.length===0 && <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Commission claims are auto-created when a semester becomes ENROLLED.</div>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents">
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="text-xs text-slate-500">Submission / Approval / Rejection letters are captured automatically inside the Filing status modal. Use the buttons below for anything else.</div>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" data-testid="b2c-file-input" accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
              <Button size="sm" data-testid="b2c-doc-upload-general" onClick={()=>pickAndUpload("document")}><Upload className="w-4 h-4 mr-1"/>General Upload</Button>
              <Button size="sm" variant="outline" data-testid="b2c-doc-pal" onClick={()=>pickAndUpload("pal")}>PAL</Button>
              <Button size="sm" variant="outline" data-testid="b2c-doc-enrollment-proof" onClick={()=>pickAndUpload("enrollment_proof")}>Enrollment Proof</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-3">File</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Uploaded</th><th></th></tr></thead>
              <tbody>
                {data.documents.map(d => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400"/>{d.original_filename}</td>
                    <td className="px-4 py-3 capitalize">{d.doc_type?.replace(/_/g," ")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={()=>downloadDoc(d)}><Download className="w-4 h-4"/></Button></td>
                  </tr>
                ))}
                {data.documents.length===0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No documents.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <ol className="relative border-l border-slate-200 space-y-3 pl-4">
              {data.activity.map(ev => (
                <li key={ev.id}>
                  <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-orange-500" />
                  <div className="text-xs text-slate-500">{new Date(ev.created_at).toLocaleString()} · <b>{ev.actor_name}</b> · {ev.entity}</div>
                  <div className="text-sm">{ev.action.replace(/_/g," ")} {ev.old_value != null && ev.new_value != null ? <span className="text-slate-500">({JSON.stringify(ev.old_value)} → {JSON.stringify(ev.new_value)})</span> : null}</div>
                </li>
              ))}
              {data.activity.length===0 && <li className="text-slate-500 text-sm">No activity.</li>}
            </ol>
          </div>
        </TabsContent>
      </Tabs>

      {/* Handoff dialog */}
      <Dialog open={handoffOpen} onOpenChange={setHandoffOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Move to Filing</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to move this application to Filing?</p>
          <div><Label>Filing Officer *</Label>
            <Select value={handoffFO} onValueChange={setHandoffFO}>
              <SelectTrigger data-testid="handoff-fo-select"><SelectValue placeholder="Choose"/></SelectTrigger>
              <SelectContent>{filingOfficers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}{filingOfficers.length===0 && <div className="p-3 text-sm text-slate-500">No active B2C filing officers. Add one in Users.</div>}</SelectContent>
            </Select>
          </div>
          {a.stage === "Admission Complete / PAL Awaited" && <div className="text-xs text-amber-800 bg-amber-50 p-2 rounded">PAL is pending. Filing officer may prepare the file in parallel.</div>}
          <DialogFooter><Button variant="outline" onClick={()=>setHandoffOpen(false)}>Cancel</Button><Button data-testid="handoff-confirm-btn" onClick={submitHandoff} className="bg-orange-500 hover:bg-orange-600 text-white">Confirm Handoff</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filing status modal */}
      <Dialog open={!!filingModal} onOpenChange={v=>!v && setFilingModal(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Set Filing Status: {filingModal?.status}</DialogTitle></DialogHeader>
          {filingModal && (
            <div className="space-y-3">
              {filingModal.status === "Applied" && <>
                <div><Label>Submission Date *</Label><Input type="date" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, submission_date:e.target.value}})}/></div>
                <div><Label>IRCC File Number *</Label><Input data-testid="filing-ircc" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, ircc_file_number:e.target.value}})}/></div>
              </>}
              {filingModal.status === "Approved" && <>
                <div><Label>Approval Date *</Label><Input type="date" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, approval_date:e.target.value}})}/></div>
                <p className="text-xs text-slate-500">Enrollment Semester 1 (PENDING) will be auto-created.</p>
              </>}
              {filingModal.status === "Rejected" && <>
                <div><Label>Rejection Date *</Label><Input type="date" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, rejection_date:e.target.value}})}/></div>
                <div><Label>Officer Notes *</Label><Textarea onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, officer_notes:e.target.value}})}/></div>
              </>}
              {filingModal.status === "Withdrawn" && <>
                <div><Label>Withdrawal Date *</Label><Input type="date" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, withdrawal_date:e.target.value}})}/></div>
                <div><Label>Notes *</Label><Textarea onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, notes:e.target.value}})}/></div>
              </>}
              {filingModal.status === "Documents Requested" && <>
                <div><Label>Request Date *</Label><Input type="date" onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, request_date:e.target.value}})}/></div>
                <div><Label>Officer Notes *</Label><Textarea onChange={e=>setFilingModal({...filingModal, form:{...filingModal.form, officer_notes:e.target.value}})}/></div>
              </>}
              {/* Mandatory doc uploads */}
              {(REQUIRED_DOCS[filingModal.status] || []).map(rq => (
                <div key={rq.key}>
                  <Label>{rq.label}</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                         data-testid={`filing-upload-${rq.key}`}
                         onChange={e => setFilingModal({...filingModal, files: {...(filingModal.files||{}), [rq.key]: e.target.files?.[0]}})} />
                  {filingModal.files?.[rq.key] && <div className="text-xs text-emerald-700 mt-1">Selected: {filingModal.files[rq.key].name}</div>}
                </div>
              ))}
              <p className="text-xs text-amber-800 bg-amber-50 p-2 rounded">Documents shown with * are mandatory. Save will upload them and then update the status.</p>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setFilingModal(null)}>Cancel</Button><Button data-testid="filing-modal-save" onClick={submitFiling} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deferred intake modal — asks Month + Year when stage becomes Deferred */}
      <DeferredDialog
        open={deferredOpen}
        onOpenChange={setDeferredOpen}
        initialValue={a?.deferred_to || ""}
        onConfirm={confirmDeferred}
        saving={deferredSaving}
      />

      {/* Applied stage modal (College Application ID) */}
      <Dialog open={!!appliedModal} onOpenChange={v=>!v && setAppliedModal(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Mark as Applied</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>College Application ID *</Label>
              <Input data-testid="applied-college-app-id" autoFocus
                     value={appliedModal?.college_app_id || ""}
                     onChange={e=>setAppliedModal({...(appliedModal||{}), college_app_id: e.target.value})} />
              <p className="text-xs text-slate-500 mt-1">This will populate the College App ID on the application.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAppliedModal(null)}>Cancel</Button>
            <Button data-testid="applied-modal-save" onClick={submitApplied} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Semester dialog */}
      <Dialog open={!!semDialog} onOpenChange={v=>!v && setSemDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{semDialog?.mode === "create" ? `Create Semester ${semDialog?.form.semester_number}` : `Update Semester ${semDialog?.sem.semester_number} → ${semDialog?.form.status}`}</DialogTitle></DialogHeader>
          {semDialog?.mode === "create" && (
            <div><Label>Intake</Label>
              <Select value={semDialog.form.intake_id||""} onValueChange={v=>setSemDialog({...semDialog, form:{...semDialog.form, intake_id:v}})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {semDialog?.mode === "update" && semDialog.form.status === "ENROLLED" && (
            <div><Label>Confirmed Intake *</Label>
              <Select onValueChange={v=>setSemDialog({...semDialog, form:{...semDialog.form, confirmation_intake_id:v}})}>
                <SelectTrigger data-testid="enroll-confirm-intake"><SelectValue placeholder="Select intake student is enrolling into"/></SelectTrigger>
                <SelectContent>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">The intake's start date becomes the confirmation date for invoicing.</p>
            </div>
          )}
          {semDialog?.mode === "update" && semDialog.form.status === "DEFERRED" && (
            <>
              <div><Label>Deferred Intake *</Label>
                <Select onValueChange={v=>setSemDialog({...semDialog, form:{...semDialog.form, deferred_intake_id:v}})}>
                  <SelectTrigger data-testid="defer-intake"><SelectValue placeholder="Choose new intake"/></SelectTrigger>
                  <SelectContent>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea onChange={e=>setSemDialog({...semDialog, form:{...semDialog.form, notes:e.target.value}})}/></div>
              <p className="text-xs text-slate-500">Original intake will be retained in history.</p>
            </>
          )}
          {semDialog?.mode === "update" && semDialog.form.status === "WITHDRAWN" && (
            <>
              <div><Label>Withdrawal Date *</Label><Input type="date" onChange={e=>setSemDialog({...semDialog, form:{...semDialog.form, withdrawal_date:e.target.value}})}/></div>
              <div><Label>Reason *</Label><Textarea data-testid="withdraw-reason" onChange={e=>setSemDialog({...semDialog, form:{...semDialog.form, withdrawal_reason:e.target.value}})}/></div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSemDialog(null)}>Cancel</Button>
            <Button data-testid="sem-dialog-save" onClick={async()=>{
              try {
                if (semDialog.mode === "create") await api.post(`/b2c/applications/${id}/semesters`, semDialog.form);
                else await api.patch(`/b2c/semesters/${semDialog.sem.id}`, semDialog.form);
                toast.success("Saved"); setSemDialog(null); load();
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!commReceivedDialog} onOpenChange={o=>!o && setCommReceivedDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Mark Semester {commReceivedDialog?.semester} Commission Received</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Received Date *</Label>
              <Input
                data-testid="comm-received-date"
                type="date"
                value={commReceivedDialog?.date || ""}
                onChange={e=>setCommReceivedDialog({...commReceivedDialog, date:e.target.value})}
              />
            </div>
            <div>
              <Label>Amount Received (CAD)</Label>
              <Input
                data-testid="comm-received-amount"
                type="number"
                value={commReceivedDialog?.amount ?? ""}
                onChange={e=>setCommReceivedDialog({...commReceivedDialog, amount:e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">Defaults to expected amount ({commReceivedDialog?.expected != null ? CAD(commReceivedDialog.expected) : "—"}). Edit if different.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCommReceivedDialog(null)}>Cancel</Button>
            <Button
              data-testid="comm-received-save"
              onClick={async()=>{
                if (!commReceivedDialog?.date) { toast.error("Received date is required"); return; }
                const amt = commReceivedDialog.amount === "" || commReceivedDialog.amount == null ? commReceivedDialog.expected : Number(commReceivedDialog.amount);
                try {
                  await api.patch(`/b2c/commission-claims/${commReceivedDialog.claim_id}`, {
                    status: "COMMISSION RECEIVED",
                    amount_received: amt,
                    commission_received_date: commReceivedDialog.date,
                  });
                  toast.success("Marked as received");
                  setCommReceivedDialog(null);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Reassign Admission Officer</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Assign to</Label>
            <Select value={reassignOfficer} onValueChange={setReassignOfficer}>
              <SelectTrigger data-testid="reassign-officer-select"><SelectValue placeholder="Choose officer"/></SelectTrigger>
              <SelectContent>
                {admissionsOfficers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Current: {meta.users.find(u=>u.user_id===a.admission_officer_id)?.name || "Unassigned"}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReassignOpen(false)}>Cancel</Button>
            <Button
              data-testid="reassign-save"
              onClick={async()=>{
                if (!reassignOfficer) { toast.error("Pick an officer"); return; }
                try {
                  await api.patch(`/b2c/applications/${id}`, { admission_officer_id: reassignOfficer });
                  toast.success("Officer reassigned");
                  setReassignOpen(false);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocViewerModal open={!!docViewer} onOpenChange={(v)=>{ if (!v) setDocViewer(null); }} url={docViewer?.url} title={docViewer?.title} />
    </div>
  );
}

const Info = ({label, value, accented}) => (<div className={`bg-white p-4 rounded-xl border ${accented ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`}><div className="text-xs uppercase text-slate-500 flex items-center gap-1.5">{label}{accented && <span className="stage-badge bg-emerald-50 text-emerald-900 border-emerald-300 text-[10px]">Accepted</span>}</div><div className="font-medium mt-1">{value || "—"}</div></div>);
const F = ({label, children}) => (<div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
